import { getSchema, type JSONContent } from '@tiptap/core';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';

import { PRESENTATION_TEXT_BLOCK_EXTENSIONS } from '~/components/v2/components/showcase/presentationTextBlockExtensions';

const isTextBlockDocument = (value: unknown): value is JSONContent => {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'doc';
};

/**
 * Parses a presentation text block's wire `body` into a ProseMirror document, or null when it
 * can't. The single read-side counterpart to the editor's write contract
 * (`JSON.stringify(editor.getJSON())`): the backend stores `body` as a JSON-STRINGIFIED
 * ProseMirror document, so strings are parsed before the structural doc check. Already-parsed
 * objects passing the check are accepted too — the liberal read keeps tests/fixtures simple and
 * the door open if the wire ever moves to raw JSON. Anything else (malformed JSON, non-doc
 * values) returns null so a bad body renders nothing instead of crashing a public page.
 *
 * Every surface that reads a text block body (the public viewer today, the collection-link
 * editor next) must parse through THIS function so the read contract can never drift.
 */
export const parsePresentationTextBlockBody = (body: unknown): JSONContent | null => {
  if (typeof body === 'string') {
    try {
      const parsed: unknown = JSON.parse(body);

      return isTextBlockDocument(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return isTextBlockDocument(body) ? body : null;
};

const PRESENTATION_TEXT_BLOCK_SCHEMA = getSchema(PRESENTATION_TEXT_BLOCK_EXTENSIONS);

/**
 * Structural parse PLUS full schema validation. The structural check above can't see
 * out-of-schema nodes/marks inside an otherwise well-formed doc. TipTap's static renderer now
 * routes unknown types through `unhandledNode` instead of throwing, so the public viewer must
 * validate here too — otherwise an extra node (e.g. `iframeEmbed`) would skip the whole-block
 * drop and still paint the rest of the doc. Feeding one to `useEditor` / `setContent` still
 * throws and would crash the collection-link editor, so seeding sites also call this and fall
 * back to an empty document (the author sees an empty editable block, mirroring the viewer
 * rendering nothing).
 */
export const parseSeedablePresentationTextBlockBody = (body: unknown): JSONContent | null => {
  const textBlockDocument = parsePresentationTextBlockBody(body);

  if (!textBlockDocument) {
    return null;
  }

  try {
    ProseMirrorNode.fromJSON(PRESENTATION_TEXT_BLOCK_SCHEMA, textBlockDocument);

    return textBlockDocument;
  } catch {
    return null;
  }
};
